const MIN_CHARS_FOR_TEXT_LAYER = 120;
const PDF_RENDER_SCALE = 2.5;

const textLayerLooksUsable = (text) => {
  const trimmed = (text || "").trim();
  if (trimmed.length < MIN_CHARS_FOR_TEXT_LAYER) return false;

  const hasCnicPattern = /\d{5}[-\s.]?\d{7}[-\s.]?\d/.test(trimmed);
  const hasIdentityLabels =
    /national identity card/i.test(trimmed) &&
    /(name|father|identity number|date of birth)/i.test(trimmed);

  if (hasIdentityLabels && !hasCnicPattern) return false;
  if (hasCnicPattern && trimmed.length >= MIN_CHARS_FOR_TEXT_LAYER) return true;

  return trimmed.length >= 500;
};

const loadPdfJs = async () => {
  const pdfjsLib = await import("pdfjs-dist");
  const version = pdfjsLib.version || "4.10.38";
  const localWorker = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;
  const cdnWorker = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
  pdfjsLib.GlobalWorkerOptions.workerSrc = localWorker;

  try {
    const res = await fetch(localWorker, { method: "HEAD" });
    if (!res.ok) pdfjsLib.GlobalWorkerOptions.workerSrc = cdnWorker;
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = cdnWorker;
  }

  return pdfjsLib;
};

/**
 * @param {import("pdfjs-dist").PDFDocumentProxy} doc
 * @param {number} pageNumber
 * @returns {Promise<Blob>}
 */
const renderPdfPageToBlob = async (doc, pageNumber) => {
  const page = await doc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not render PDF page.");

  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to render PDF page"))),
      "image/png",
      1,
    );
  });
};

/**
 * @param {ArrayBuffer} arrayBuffer
 * @param {{ renderImages?: boolean }} [options]
 * @returns {Promise<import("./types").DocumentPage[]>}
 */
export async function ingestPdf(arrayBuffer, options = {}) {
  const { renderImages = true } = options;
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    throw new Error("Received empty buffer");
  }

  const pdfjsLib = await loadPdfJs();
  const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const totalPages = doc.numPages;
  const pages = [];

  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    let pageText = "";
    try {
      const page = await doc.getPage(pageNumber);
      const textContent = await page.getTextContent();
      pageText = textContent.items
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
    } catch {
      pageText = "";
    }

    if (textLayerLooksUsable(pageText)) {
      pages.push({ pageNumber, text: pageText, image: null });
      continue;
    }

    if (!renderImages) {
      pages.push({ pageNumber, text: null, image: null });
      continue;
    }

    try {
      const image = await renderPdfPageToBlob(doc, pageNumber);
      pages.push({ pageNumber, text: null, image });
    } catch (err) {
      console.error(`Failed to render PDF page ${pageNumber}:`, err);
      pages.push({ pageNumber, text: pageText || null, image: null });
    }
  }

  return pages;
}
